package com.bulut.mecitogullari.data.model

import kotlinx.serialization.Serializable

/** One building of a project; each block has its own layout. */
@Serializable
data class ProjectBlock(
    /** "A", "B"… Blank when the project has a single, unnamed block. */
    val name: String = "",
    val floors: Int = 0,
    val apartmentsPerFloor: Int = 0,
) {
    val totalUnits: Int get() = floors * apartmentsPerFloor
}

/** A construction project (site), laid out as one or more blocks. */
@Serializable
data class Project(
    val id: String,
    val name: String,
    val location: String = "",
    /** UTC-midnight epoch millis; null when unknown. */
    @Serializable(with = LongAsDoubleSerializer::class)
    val startDateEpochMillis: Long? = null,
    @Serializable(with = LongAsDoubleSerializer::class)
    val expectedEndEpochMillis: Long? = null,
    val floors: Int = 0,
    val apartmentsPerFloor: Int = 0,
    /**
     * The site's blocks, each with its own floor count and units per floor.
     * Empty on records created before blocks existed; those fall back to the
     * legacy [floors]/[apartmentsPerFloor] pair as one unnamed block.
     */
    val blocks: List<ProjectBlock> = emptyList(),
    val notes: String = "",
    /** Accent color used on cards (stands in for an optional cover image). */
    val coverColorHex: String = "#1E293B",
    /** Ids of finished [ConstructionStages] — drives the progress percent. */
    val completedStages: List<String> = emptyList(),
    @Serializable(with = LongAsDoubleSerializer::class)
    val createdAtEpochMillis: Long,
) {
    /** Blocks to lay out; a single unnamed block for legacy projects. */
    val effectiveBlocks: List<ProjectBlock>
        get() = blocks.ifEmpty {
            listOf(ProjectBlock(name = "", floors = floors, apartmentsPerFloor = apartmentsPerFloor))
        }

    val blockCount: Int get() = effectiveBlocks.size

    val totalUnits: Int get() = effectiveBlocks.sumOf { it.totalUnits }

    /** 0..100, sum of the weights of completed stages. */
    val progressPercent: Int get() = ConstructionStages.progressOf(completedStages)
}

data class ConstructionStage(
    val id: String,
    val label: String,
    /** Share of the total build, all stages sum to 100. */
    val weight: Int,
)

/** Rough construction phases — enough to answer "yüzde kaçı bitti?". */
object ConstructionStages {
    val all = listOf(
        ConstructionStage("ruhsat", "Arsa & Ruhsat", 5),
        ConstructionStage("temel", "Zemin & Temel", 15),
        ConstructionStage("kaba", "Kaba İnşaat", 30),
        ConstructionStage("cati", "Çatı", 5),
        ConstructionStage("siva", "Duvar & Sıva", 10),
        ConstructionStage("tesisat", "Elektrik & Tesisat", 10),
        ConstructionStage("ince", "İç Kaplama & Boya", 15),
        ConstructionStage("cephe", "Dış Cephe", 5),
        ConstructionStage("iskan", "İskan & Teslim", 5),
    )

    fun progressOf(completedIds: List<String>): Int =
        all.filter { it.id in completedIds }.sumOf { it.weight }.coerceIn(0, 100)
}
